import React from 'react';
import { Fade, HStack, useDisclosure } from '@cardboard-ui/react';
import { t } from '@lingui/macro';
import { EditPencilIcon } from 'components/icons';
import { OptionsMenu } from 'components/OptionsMenu';
import { DeleteOption } from 'components/OptionsMenu/options/DeleteOption';
import { OptionsMenuButton } from 'components/OptionsMenu/OptionsMenuButton';
import { OptionsMenuItem } from 'components/OptionsMenu/OptionsMenuItem';
import { graphql, useFragment, useMutation } from 'utils/graphClient';
import { AnnouncementMessageActions_chat_messageFragment$key } from './__generated__/AnnouncementMessageActions_chat_messageFragment.graphql';
import { AnnouncementMessageActions_chat_deleteMessage_Mutation } from './__generated__/AnnouncementMessageActions_chat_deleteMessage_Mutation.graphql';
import { AnnouncementReactionButton } from './AnnouncementReactionButton';

const messageFragment = graphql`
  fragment AnnouncementMessageActions_chat_messageFragment on Message {
    id
    ...AnnouncementReactionButton_chat_messageFragment
    deletedAt
    canUpdate {
      value
    }
    canDelete {
      value
    }
    reactions {
      edges {
        node {
          id
          deletedAt
        }
      }
    }
  }
`;

const deleteMessageMutation = graphql`
  mutation AnnouncementMessageActions_chat_deleteMessage_Mutation($id: ID!) {
    deleteMessage(input: { messageId: $id }) {
      ...AnnouncementMessageActions_chat_messageFragment
    }
  }
`;

interface MessageActionsProps {
  message: AnnouncementMessageActions_chat_messageFragment$key;
  setIsEditingMessage: (isEditingMessage: boolean) => void;
  keepScrollAtTheBottom: () => void;
  allowReactions: boolean;
  allowDeletionOrModification: boolean;
}

export const AnnouncementMessageActions: React.FC<MessageActionsProps> = ({
  message,
  setIsEditingMessage,
  keepScrollAtTheBottom,
  allowReactions,
  allowDeletionOrModification,
}) => {
  const { id, canDelete, canUpdate, reactions, ...fragments } = useFragment(
    messageFragment,
    message,
  );

  const [deleteMessage, deleteMessageLoading] =
    useMutation<AnnouncementMessageActions_chat_deleteMessage_Mutation>(
      deleteMessageMutation,
    );

  const { isOpen, onClose, onOpen } = useDisclosure();
  const hasReactions =
    reactions.edges.filter(({ node }) => !node.deletedAt).length > 0;
  return (
    <HStack spacing={1}>
      <Fade in={!hasReactions && allowReactions}>
        <AnnouncementReactionButton
          message={fragments}
          keepScrollAtTheBottom={keepScrollAtTheBottom}
        />
      </Fade>
      {allowDeletionOrModification ? (
        <OptionsMenu
          button={
            <OptionsMenuButton
              onClick={onOpen}
              variant="solid"
              colorScheme="gray"
              size="xs"
              borderRadius="full"
              // _hover={{ bg: 'gray.100' }}
              // _expanded={{ bg: 'blue.100' }}
            />
          }
          isOpen={isOpen}
          onClose={onClose}
        >
          <OptionsMenuItem
            icon={<EditPencilIcon />}
            onClick={() => {
              setIsEditingMessage(true);
              onClose();
            }}
            isDisabled={!canUpdate.value}
          >
            {t`Edit Message`}
          </OptionsMenuItem>
          <DeleteOption
            isDisabled={!canDelete.value}
            isDeleting={deleteMessageLoading}
            onDelete={(onCompleted) => {
              deleteMessage({
                variables: {
                  id,
                },
                onCompleted,
              });
            }}
          />
        </OptionsMenu>
      ) : null}
    </HStack>
  );
};
